/**
 * Rejeu du fichier GPX source à l'export.
 *
 * Portage de `GpxPatcher.kt`. L'export ne re-sérialise plus le modèle mémoire :
 * il repart de la copie du fichier importé et n'y réécrit que **cinq régions**.
 * Tout le reste passe octet pour octet — `<metadata>`, sections `CDATA`,
 * `<rte>`, `<sym>`, horodatages, extensions d'autres applications posées sur la
 * trace comme sur les points.
 *
 * Ce n'est pas un raffinement : sur les 199 GPX du poste de l'auteur, 79
 * portent un `<metadata>`, 121 des horodatages, 76 des points d'intérêt, 41 des
 * `<sym>`. Rien de tout cela ne survivait à un aller-retour, si bien qu'un GPX
 * d'organisateur annoté puis renvoyé n'était plus le sien.
 *
 * Les cinq régions :
 *
 *  1. la balise `<gpx>` — `creator` remplacé, `xmlns:velogpx` ajouté s'il
 *     manque et si un bloc doit être écrit ;
 *  2. le `<name>` du `<trk>` retenu ;
 *  3. les enfants `velogpx:*` de son bloc `<extensions>` ;
 *  4. ses `<trkseg>`, **seulement si la géométrie a bougé** ;
 *  5. les autres `<trk>`, qui partent — un fichier exporté n'en porte qu'un.
 *
 * Le balayage est propre à ce module plutôt que confié à `scanXml` : il faut
 * des **positions** dans la source et la **profondeur** des éléments.
 * `<extensions>` existe aussi sous `<trkpt>` et `<name>` sous `<wpt>` : un
 * repérage à plat viserait le mauvais.
 *
 * Référence : `GpxPatcher.kt`, `docs/gpx-extensions.md` § « Export : rejeu du
 * fichier source », et le jeu de référence partagé
 * `app/src/test/resources/gpx/export/`.
 */

import { VELOGPX_NAMESPACE } from './gpxParser'
import {
  hasVeloGpxExtensions,
  writeExtensionsBlock,
  writeSegments,
  writeVeloGpxChildren,
  type TrackToWrite,
} from './gpxWriter'
import { escapeXmlAttribute, escapeXmlText } from './xml'

/**
 * Indentation ajoutée d'un cran quand la source n'en donne pas l'exemple.
 *
 * Quatre espaces, comme `GpxPatcher.kt` — et non les deux de `writeGpx` : ce
 * n'est pas le même chemin, et cette valeur ne sert que de repli. L'indentation
 * du fichier patché prime partout où il en offre une.
 */
const STEP = '    '

/**
 * Plafond de la source relue, en **unités de code UTF-16**.
 *
 * Seize millions d'unités, soit environ 32 Mo de mémoire pour la seule chaîne,
 * et de l'ordre du double une fois le résultat composé : la source est relue en
 * entier puis recopiée. Les 200 000 points du plafond du parseur pèsent environ
 * 12 millions de caractères ; au-delà, le fichier n'est plus une trace
 * plausible et le repli sur `writeGpx` prend la main.
 *
 * Second garde-fou sous celui de l'import, qui refuse au-delà de
 * `LIMITS.MAX_GPX_FILE_SIZE` : le fichier en dépôt vient forcément de là, mais
 * un plafond exprimé en octets à l'entrée ne borne pas la chaîne décodée qui
 * en sort, et c'est elle qu'on manipule ici.
 */
export const MAX_PATCH_SOURCE_LENGTH = 16 * 1024 * 1024

/**
 * Fin de ligne du fichier, telle qu'il l'emploie.
 *
 * Un GPX produit sous Windows est en CRLF. Y injecter des blocs en LF donnerait
 * un fichier aux fins de ligne mêlées — et ce module existe pour rendre le
 * fichier qu'on lui a donné, pas une version retouchée. La première occurrence
 * fait foi : un fichier qui mêlerait déjà les deux n'a pas de convention à
 * respecter.
 */
function lineEndingOf(source: string): string {
  const newline = source.indexOf('\n')
  return newline > 0 && source[newline - 1] === '\r' ? '\r\n' : '\n'
}

/**
 * Réécrit un bloc fraîchement produit avec la fin de ligne du fichier.
 *
 * Sûr sans condition : ces blocs viennent de `gpxWriter`, qui n'émet que des
 * `\n`. On ne touche jamais aux octets de la source par ce biais.
 */
function withEol(block: string, eol: string): string {
  return eol === '\n' ? block : block.replace(/\n/g, eol)
}

/** Déclaration d'espace de noms, telle qu'elle est ajoutée à `<gpx>`. */
const VELOGPX_NAMESPACE_ATTR = `xmlns:velogpx="${VELOGPX_NAMESPACE}"`

interface Attribute {
  readonly name: string
  /** Valeur **brute**, telle que la source la porte : jamais réechappée. */
  readonly value: string
  readonly quote: string
}

type TagKind = 'open' | 'close' | 'self' | 'other'

interface Tag {
  readonly kind: TagKind
  readonly name: string
  readonly start: number
  readonly end: number
}

interface Element {
  readonly name: string
  /** Début de la balise ouvrante. */
  readonly start: number
  /** Fin de la balise ouvrante (index après son `>`). */
  readonly openEnd: number
  /** Début de la balise fermante — égal à `end` si l'élément est auto-fermant. */
  readonly closeStart: number
  /** Fin de l'élément entier. */
  readonly end: number
}

interface Edit {
  readonly start: number
  readonly end: number
  readonly text: string
}

export interface PatchInput {
  /** Contenu du GPX source, tel que le dépôt le garde. */
  readonly source: string
  /** Position du `<trk>` retenu **dans le fichier**, avant tout filtrage. */
  readonly fileTrackIndex: number
  readonly track: TrackToWrite
  readonly creator: string
  /**
   * La géométrie a-t-elle été remplacée depuis la lecture du fichier ?
   *
   * Faux, les `<trkseg>` d'origine passent tels quels, **avec les extensions
   * de leurs points** — cardio, cadence — que le modèle ne porte pas. Vrai,
   * ils sont réécrits depuis le modèle et ces extensions tombent avec les
   * points qu'elles annotaient : c'est le prix d'une géométrie qui n'est plus
   * celle du fichier, et il n'y a pas de moyen de le payer moins cher.
   */
  readonly geometryModified: boolean
}

/**
 * Rejoue le fichier source en n'y remplaçant que ce qui a changé.
 *
 * Rend `null` — et jamais une exception — quand le rejeu n'est pas possible.
 * L'appelant retombe alors sur `writeGpx`, qui reconstruit un document depuis
 * le modèle : on y perd la fidélité, jamais l'export.
 */
export function patchGpx(input: PatchInput): string | null {
  try {
    return patchOrNull(input)
  } catch (error) {
    // Le patcheur travaille sur un fichier que le parseur a déjà accepté, mais
    // il ne le revalide pas : un cas non prévu doit coûter la fidélité, pas
    // l'export.
    console.error('[VeloGPX] Rejeu du fichier source impossible', error)
    return null
  }
}

function patchOrNull(input: PatchInput): string | null {
  const { source, track, creator, geometryModified } = input

  if (source.length > MAX_PATCH_SOURCE_LENGTH) return null

  const gpxTag = findGpxOpenTag(source)
  if (!gpxTag) return null

  const gpxCloseStart = source.lastIndexOf('</gpx>')
  if (gpxCloseStart < gpxTag.end) return null

  const trks = directChildren(source, gpxTag.end, gpxCloseStart).filter(
    (element) => element.name === 'trk',
  )

  // Un index hors bornes ne rend pas une autre trace en silence : c'est le
  // repli, donc un fichier reconstruit, ce qui se voit — là où exporter le
  // mauvais `<trk>` ne se verrait pas.
  const target = trks[input.fileTrackIndex]
  if (!target) return null

  // `<trk/>` auto-fermant : il n'y a ni `<name>`, ni `<extensions>`, ni
  // `<trkseg>` où écrire quoi que ce soit.
  if (target.openEnd === target.end) return null

  const edits: Edit[] = []
  const needsNamespace = hasVeloGpxExtensions(track)
  const eol = lineEndingOf(source)

  edits.push({
    start: gpxTag.start,
    end: gpxTag.end,
    text: rebuildGpxTag(source, gpxTag, creator, needsNamespace),
  })

  // Les autres traces partent avec le saut de ligne qui les précède, sans quoi
  // le fichier se remplit de lignes vides.
  for (const other of trks) {
    if (other === target) continue
    edits.push({
      start: lineStartOf(source, other.start),
      end: other.end,
      text: '',
    })
  }

  const trkChildren = directChildren(source, target.openEnd, target.closeStart)
  const trkIndent = indentAt(source, target.start)
  const firstChild = trkChildren[0]
  const childIndent = firstChild
    ? indentAt(source, firstChild.start)
    : trkIndent + STEP

  editTrackName(target, trkChildren, childIndent, track, edits, eol)
  editExtensions(source, target, trkChildren, childIndent, track, edits, eol)
  if (geometryModified) {
    editSegments(trkChildren, childIndent, track, edits, eol)
  }

  return applyEdits(source, edits)
}

/* -------------------------------------------------------------------------
 * Les cinq régions
 * ---------------------------------------------------------------------- */

/**
 * Réécrit la balise `<gpx>`, sur une seule ligne.
 *
 * C'est le **seul** endroit où la mise en forme du fichier d'origine est
 * perdue : une balise `<gpx>` étalée sur quatre lignes ressort sur une. Les
 * valeurs d'attributs, elles, sont recopiées **brutes**, avec leur guillemet
 * d'origine — les réechapper transformerait un `&amp;` déjà correct en
 * `&amp;amp;` à chaque export, et le fichier se dégraderait à chaque
 * aller-retour.
 */
function rebuildGpxTag(
  source: string,
  tag: Tag,
  creator: string,
  addNamespace: boolean,
): string {
  const attributes = parseAttributes(source, tag)
  const parts: string[] = []
  let creatorWritten = false
  let hasNamespace = false

  for (const attribute of attributes) {
    if (attribute.name === 'creator') {
      // Le `creator` vient de l'application, pas du fichier : lui, s'échappe.
      parts.push(`creator="${escapeXmlAttribute(creator)}"`)
      creatorWritten = true
      continue
    }
    if (attribute.name === 'xmlns:velogpx') hasNamespace = true
    parts.push(`${attribute.name}=${attribute.quote}${attribute.value}${attribute.quote}`)
  }

  if (!creatorWritten) {
    parts.push(`creator="${escapeXmlAttribute(creator)}"`)
  }
  if (addNamespace && !hasNamespace) {
    parts.push(VELOGPX_NAMESPACE_ATTR)
  }

  return `<gpx ${parts.join(' ')}>`
}

/**
 * Remplace le `<name>` de la trace, ou l'insère s'il manque.
 *
 * Une section `CDATA` devient du texte échappé : c'est un nom que
 * l'utilisateur a pu changer, il ne peut pas ressortir tel quel. Inséré, il
 * devient le **premier** enfant du `<trk>` — seule position que le schéma
 * accepte.
 */
function editTrackName(
  target: Element,
  trkChildren: readonly Element[],
  childIndent: string,
  track: TrackToWrite,
  edits: Edit[],
  eol: string,
): void {
  const rendered = `<name>${escapeXmlText(track.name)}</name>`
  const existing = trkChildren.find((element) => element.name === 'name')

  if (existing) {
    edits.push({ start: existing.start, end: existing.end, text: rendered })
    return
  }

  edits.push({
    start: target.openEnd,
    end: target.openEnd,
    text: `${eol}${childIndent}${rendered}`,
  })
}

/**
 * Pose les enfants `velogpx:*` dans le bloc `<extensions>` du fichier.
 *
 * Trois cas, et le premier invariant est le même dans les trois : **un seul
 * bloc `<extensions>` par élément**, celui du fichier étant réutilisé plutôt
 * que doublé. Les extensions tierces qui s'y trouvent restent, et les enfants
 * `velogpx:*` arrivent après elles.
 *
 * Les anciens enfants `velogpx:*` sont retirés **avant** de décider s'il y a
 * quelque chose à écrire : réexporter une trace dont on a supprimé toutes les
 * annotations doit bien vider le bloc.
 */
function editExtensions(
  source: string,
  target: Element,
  trkChildren: readonly Element[],
  childIndent: string,
  track: TrackToWrite,
  edits: Edit[],
  eol: string,
): void {
  const existing = trkChildren.find((element) => element.name === 'extensions')

  if (!existing) {
    const block = withEol(writeExtensionsBlock(track, childIndent, STEP), eol)
    if (block.length === 0) return

    // Juste avant le premier `<trkseg>` : le schéma veut `<extensions>` après
    // les métadonnées du `<trk>` et avant sa géométrie.
    const firstSeg = trkChildren.find((element) => element.name === 'trkseg')
    if (firstSeg) {
      const anchor = firstSeg.start - indentAt(source, firstSeg.start).length
      edits.push({ start: anchor, end: anchor, text: block })
      return
    }

    // Sans `<trkseg>`, il ne reste que la fin de l'élément — et il faut alors
    // rendre à `</trk>` l'indentation qu'on lui prend : ancrer sur sa balise
    // seule laissait le bloc collé derrière elle et la fermeture rejetée en
    // colonne zéro. Le XML restait valide, la mise en forme non.
    // L'insertion se fait au **début de la ligne** de `</trk>`, dont
    // l'indentation reste en place derrière : ancrer sur la balise elle-même
    // laissait le bloc collé derrière elle et la fermeture rejetée en colonne
    // zéro. Le XML restait valide, la mise en forme non.
    const anchor =
      target.closeStart - indentAt(source, target.closeStart).length
    edits.push({ start: anchor, end: anchor, text: block })
    return
  }

  // `<extensions/>` auto-fermant : rien à conserver, on le remplace en entier.
  if (existing.openEnd === existing.end) {
    const block = withEol(
      writeExtensionsBlock(track, indentAt(source, existing.start), STEP),
      eol,
    )
    if (block.length === 0) return
    edits.push({
      start: lineStartOf(source, existing.start),
      end: existing.end,
      text: `${eol}${block.slice(0, -eol.length)}`,
    })
    return
  }

  const extChildren = directChildren(
    source,
    existing.openEnd,
    existing.closeStart,
  )

  for (const child of extChildren) {
    if (!child.name.startsWith('velogpx:')) continue
    edits.push({
      start: lineStartOf(source, child.start),
      end: child.end,
      text: '',
    })
  }

  const firstForeign = extChildren.find(
    (child) => !child.name.startsWith('velogpx:'),
  )
  const grandChildIndent = firstForeign
    ? indentAt(source, firstForeign.start)
    : indentAt(source, existing.start) + STEP

  const children = withEol(
    writeVeloGpxChildren(track, grandChildIndent, STEP),
    eol,
  )
  if (children.length === 0) return

  // Juste après le dernier saut de ligne qui précède `</extensions>` : les
  // enfants velogpx arrivent donc en queue de bloc, sous les extensions
  // tierces qui restent en tête.
  const insertAt =
    existing.closeStart - indentAt(source, existing.closeStart).length
  edits.push({ start: insertAt, end: insertAt, text: children })
}

/**
 * Remplace tous les `<trkseg>` d'un bloc, du premier au dernier.
 *
 * D'un bloc, et non un par un : ce qui se trouvait **entre** deux segments
 * décrivait l'ancienne géométrie et n'a plus de sens sous la nouvelle. C'est
 * la découpe du modèle qui fait foi — celle du fichier pour une récupération
 * d'altitude, un segment unique pour un itinéraire routier.
 */
function editSegments(
  trkChildren: readonly Element[],
  childIndent: string,
  track: TrackToWrite,
  edits: Edit[],
  eol: string,
): void {
  const segs = trkChildren.filter((element) => element.name === 'trkseg')
  if (segs.length === 0) return

  const body = withEol(writeSegments(track, childIndent, STEP), eol)
  if (body.length === 0) return

  // La source fournit déjà l'indentation du premier segment et le saut de
  // ligne qui suit le dernier.
  const text = body.slice(childIndent.length, body.length - eol.length)
  edits.push({ start: segs[0]!.start, end: segs[segs.length - 1]!.end, text })
}

/* -------------------------------------------------------------------------
 * Application des remplacements
 * ---------------------------------------------------------------------- */

/**
 * Applique les remplacements de gauche à droite.
 *
 * Le tri est stable et le curseur ne recule jamais : un remplacement qui
 * chevaucherait le précédent est **ignoré** plutôt que d'être appliqué au
 * milieu d'un autre, ce qui produirait un fichier malformé. Aucun cas connu ne
 * l'atteint — les cinq régions sont disjointes par construction —, c'est un
 * garde-fou, pas un mécanisme.
 */
function applyEdits(source: string, edits: readonly Edit[]): string {
  const sorted = [...edits].sort((a, b) => a.start - b.start)
  const out: string[] = []
  let cursor = 0

  for (const edit of sorted) {
    if (edit.start < cursor) continue
    out.push(source.slice(cursor, edit.start), edit.text)
    cursor = edit.end
  }

  out.push(source.slice(cursor))
  return out.join('')
}

/* -------------------------------------------------------------------------
 * Balayage
 * ---------------------------------------------------------------------- */

function findGpxOpenTag(source: string): Tag | null {
  let index = 0

  while (index < source.length) {
    const lt = source.indexOf('<', index)
    if (lt === -1) return null

    const tag = readTag(source, lt)
    if (!tag) return null

    if (tag.kind === 'open' && tag.name === 'gpx') return tag
    index = tag.end
  }

  return null
}

/**
 * Lit la balise qui commence à `lt`.
 *
 * Commentaires, sections `CDATA`, instructions de traitement et déclarations
 * sont franchis **d'un bloc** : leur contenu peut porter des `<` et des `>` qui
 * ne sont pas du balisage, et le fichier de référence en contient justement.
 */
function readTag(source: string, lt: number): Tag | null {
  const skip = (opener: string, closer: string): Tag | null => {
    const end = source.indexOf(closer, lt + opener.length)
    if (end === -1) return null
    return {
      kind: 'other',
      name: '',
      start: lt,
      end: end + closer.length,
    }
  }

  if (source.startsWith('<!--', lt)) return skip('<!--', '-->')
  if (source.startsWith('<![CDATA[', lt)) return skip('<![CDATA[', ']]>')
  if (source.startsWith('<?', lt)) return skip('<?', '?>')
  if (source.startsWith('<!', lt)) return skip('<!', '>')

  const isClose = source.startsWith('</', lt)
  let index = lt + (isClose ? 2 : 1)
  const nameStart = index

  while (index < source.length && !/[\s>/]/.test(source[index]!)) index++
  const name = source.slice(nameStart, index)

  // Les valeurs d'attributs sont sautées : un `>` entre guillemets ne ferme
  // pas la balise.
  while (index < source.length && source[index] !== '>') {
    const char = source[index]!
    if (char === '"' || char === "'") {
      const close = source.indexOf(char, index + 1)
      if (close === -1) return null
      index = close + 1
      continue
    }
    index++
  }

  if (index >= source.length) return null

  const selfClosing = source[index - 1] === '/'
  return {
    kind: isClose ? 'close' : selfClosing ? 'self' : 'open',
    name,
    start: lt,
    end: index + 1,
  }
}

/**
 * Les enfants **directs** de la plage, avec leurs positions.
 *
 * Directs seulement, et c'est tout l'enjeu : `<extensions>` existe aussi sous
 * `<trkpt>`, `<name>` sous `<wpt>` et sous `<metadata>`. Un repérage à plat
 * réécrirait le nom du premier point d'intérêt à la place de celui de la
 * trace.
 */
function directChildren(source: string, from: number, to: number): Element[] {
  const children: Element[] = []
  let index = from
  let depth = 0
  let openTag: Tag | null = null

  while (index < to) {
    const lt = source.indexOf('<', index)
    if (lt === -1 || lt >= to) break

    const tag = readTag(source, lt)
    if (!tag) break
    index = tag.end

    if (tag.kind === 'other') continue

    if (tag.kind === 'self') {
      if (depth === 0) {
        children.push({
          name: tag.name,
          start: tag.start,
          openEnd: tag.end,
          closeStart: tag.end,
          end: tag.end,
        })
      }
      continue
    }

    if (tag.kind === 'open') {
      if (depth === 0) openTag = tag
      depth++
      continue
    }

    // Fermante
    depth--
    if (depth < 0) break
    if (depth === 0 && openTag) {
      children.push({
        name: openTag.name,
        start: openTag.start,
        openEnd: openTag.end,
        closeStart: tag.start,
        end: tag.end,
      })
      openTag = null
    }
  }

  return children
}

/**
 * Les blancs horizontaux qui précèdent l'offset sur sa ligne.
 *
 * Chaîne vide si l'élément n'est pas seul sur sa ligne : on ne lui invente pas
 * une indentation qu'il n'a pas.
 */
function indentAt(source: string, offset: number): string {
  let index = offset
  while (index > 0 && (source[index - 1] === ' ' || source[index - 1] === '\t')) {
    index--
  }
  return index > 0 && source[index - 1] !== '\n' ? '' : source.slice(index, offset)
}

/**
 * Index du saut de ligne qui précède l'offset, ou l'offset lui-même.
 *
 * Le `\r` d'un CRLF est emporté avec le `\n` : le viser après lui laissait un
 * `\r` orphelin là où une ligne est retirée, et le fichier ressortait avec un
 * caractère que sa source n'avait pas. Mesuré sur le retrait d'un second
 * `<trk>` d'une source CRLF.
 */
function lineStartOf(source: string, offset: number): number {
  const newline = source.lastIndexOf('\n', offset)
  if (newline === -1) return offset
  return newline > 0 && source[newline - 1] === '\r' ? newline - 1 : newline
}

/**
 * Découpe les attributs de la balise, valeurs **brutes** et guillemet compris.
 *
 * Le guillemet est conservé parce qu'il fait partie de ce que le fichier dit :
 * le GPX de référence porte des `lat='45.129000'` que rien n'oblige à
 * normaliser, et les normaliser reviendrait à réécrire une région qu'on a
 * promis de ne pas toucher.
 */
function parseAttributes(source: string, tag: Tag): Attribute[] {
  const attributes: Attribute[] = []
  let index = tag.start + 1 + tag.name.length
  const limit = tag.end - 1

  while (index < limit) {
    while (index < limit && /\s/.test(source[index]!)) index++
    if (index >= limit) break

    const char = source[index]!
    if (char === '/' || char === '>') break

    const nameStart = index
    while (index < limit && !/[\s=>/]/.test(source[index]!)) index++
    const name = source.slice(nameStart, index)
    if (name.length === 0) break

    while (index < limit && /\s/.test(source[index]!)) index++

    // Attribut sans valeur : le XML l'interdit, mais on ne valide pas — on le
    // recopie sous une forme qui, elle, est valide.
    if (index >= limit || source[index] !== '=') {
      attributes.push({ name, value: '', quote: '"' })
      continue
    }

    index++
    while (index < limit && /\s/.test(source[index]!)) index++
    if (index >= limit) break

    const quote = source[index]!
    if (quote !== '"' && quote !== "'") break

    const valueStart = index + 1
    const valueEnd = source.indexOf(quote, valueStart)
    if (valueEnd === -1 || valueEnd > limit) break

    attributes.push({
      name,
      value: source.slice(valueStart, valueEnd),
      quote,
    })
    index = valueEnd + 1
  }

  return attributes
}
